import type {ExecutorPreviewData} from '@frontend/modules/public/services/ServicesModuleTypes'
import type {ApiResponseData} from '@frontend/types/AppTypes'
import type {CategoryType} from '@shared/types/Categories'
import {ApiRequestService} from 'swayok-react-mdb-ui-kit/services/ApiRequestService'
import type {FormSelectOptionsList} from 'swayok-react-mdb-ui-kit/types'
import getApiRequestOptions from '../../../helpers/getApiRequestOptions'
import type {
    AdDetailsData,
    AdPreviewDataForGrid,
    AdPreviewDataForList,
    AdPropertyConfigForFilters,
    AdsListPresentation,
    AdsLiveSearchOptionExtras,
    FilteredItemsPageUrlQueryParams,
    ItemsCategoryOrLocationForBreadcrumbs,
} from './AdsModuleTypes'

// API опубликованных объявлений.
export const PublicAdsApi = {

    // Получить настройки динамических свойств объявлений категории для формы фильтрации.
    async getCategoryAdPropertiesForFilters(
        categoryId: number | string,
        abortController?: AbortController
    ): Promise<AdPropertyConfigForFilters[]> {
        return ApiRequestService
            .get<AdPropertiesConfigsForFiltersResponseData>(
                `/v1/categories/${categoryId}/properties`,
                undefined,
                undefined,
                abortController
            )
            .then(response => response.data.properties)
    },

    // Получить общее кол-во объявлений.
    async getTotalAdsCount(abortController?: AbortController): Promise<number> {
        return ApiRequestService
            .get<ApiResponseData & {count: number;}>(
                '/v1/items/count',
                undefined,
                undefined,
                abortController
            )
            .then(response => response.data.count)
    },

    // Получить объявления для главной страницы.
    async getRandomAdsForHomePage(this: void, abortController?: AbortController): Promise<AdPreviewDataForGrid[]> {
        return ApiRequestService
            .get<AdsListResponseData>(
                '/v1/items/random',
                undefined,
                undefined,
                abortController
            )
            .then(response => response.data.records)
    },

    // Получить полные данные объявления.
    async getAdDetails(id: number, abortController?: AbortController): Promise<AdDetailsData> {
        return ApiRequestService
            .get<AdDetailsResponseData>(
                `/v1/item/${id}`,
                undefined,
                undefined,
                abortController
            )
            .then(response => response.data.advertisement)
    },

    // Получение номера телефона создателя объявления.
    // Внимание! Ошибка в названии сделана специально, чтобы усложнить слепой поиск этого метода.
    async getSellerPne(id: number, abortController?: AbortController): Promise<string> {
        // Усложняем поиск по URL.
        const url = 'v' + '1/i' + 'te' + 'm/' + id + '/' + 'p' + 'ho' + 'ne' as 'v1/item/:id/phone'
        return ApiRequestService
            .post<SellerPhoneResponseData>(
                url,
                undefined,
                await getApiRequestOptions(url),
                abortController
            )
            .then(response => response.data['p' + 'ho' + 'ne' as 'phone'])
    },

    // Получить объявления, похожие на текущее.
    async getSimilarAds(
        id: number | null,
        categoryId: number,
        limit: number,
        offset: number = 0,
        abortController?: AbortController
    ): Promise<AdPreviewDataForGrid[]> {
        return ApiRequestService
            .get<AdsListResponseData>(
                '/v1/items/similar',
                {
                    id,
                    categoryId,
                    limit,
                    offset,
                },
                undefined,
                abortController
            )
            .then(response => response.data.records)
    },

    // Получить ранее просмотренные объявления.
    async getViewedAds(
        id: number | null,
        limit: number,
        offset: number = 0,
        abortController?: AbortController
    ): Promise<AdPreviewDataForGrid[]> {
        return ApiRequestService
            .get<AdsListResponseData>(
                '/v1/items/viewed',
                {
                    id,
                    limit,
                    offset,
                },
                undefined,
                abortController
            )
            .then(response => response.data.records)
    },

    // Получить отфильтрованные объявления или услуги.
    async getFilteredItems<
        T extends AdPreviewDataForGrid | AdPreviewDataForList | ExecutorPreviewData,
    >(
        categoryId: number | string | null = null,
        categoryType: CategoryType,
        filters: FilteredItemsPageUrlQueryParams = {},
        limit: number = 20,
        offset: number = 0,
        presentation: AdsListPresentation = 'grid',
        abortController?: AbortController
    ): Promise<FilteredItemsResponseData<T>> {
        // noinspection SuspiciousTypeOfGuard
        if (filters.props && typeof filters.props === 'string') {
            // Декодируем JSON-строку с фильтрами динамических свойств в объект.
            try {
                filters.props = JSON.parse(filters.props)
            } catch (_e) {
                delete filters.props
            }
        }
        return ApiRequestService
            .get<FilteredItemsResponseData<T>>(
                // todo: add categoryType to URL.
                '/v1/items',
                {
                    ...filters,
                    categoryId,
                    categoryType,
                    limit,
                    offset,
                    presentation,
                },
                {},
                abortController
            )
            .then(response => response.data)
    },

    // Подсказки для поля ввода ключевых слов поиска по объявлениям, услугам и категориям
    // (опции для <ComboboxInput>).
    async liveSearch(
        this: void,
        keywords: string,
        limit: number = 10,
        abortController?: AbortController
    ): Promise<AdsLiveSearchResponseData['options']> {
        return ApiRequestService
            .get<AdsLiveSearchResponseData>(
                '/live-search/items',
                {
                    keywords,
                    limit,
                },
                undefined,
                abortController
            )
            .then(response => response.data.options)
    },
}

interface AdPropertiesConfigsForFiltersResponseData extends ApiResponseData {
    properties: AdPropertyConfigForFilters[]
}

export interface AdsListResponseData extends ApiResponseData {
    records: (AdPreviewDataForGrid | AdPreviewDataForList)[]
    count: number
}

export interface FilteredItemsResponseData<
    T extends AdPreviewDataForGrid | AdPreviewDataForList | ExecutorPreviewData,
> extends ApiResponseData {
    items: T[]
    itemsCount: number
    header: string
    path: ItemsCategoryOrLocationForBreadcrumbs[]
    // todo: remove after refactoring
    ads: never
    adsCount: never
    executors: never
    executorsCount: never
    executorsSubheader: never
}

interface AdDetailsResponseData extends ApiResponseData {
    advertisement: AdDetailsData
}

interface SellerPhoneResponseData extends ApiResponseData {
    phone: string
}

interface AdsLiveSearchResponseData extends ApiResponseData {
    options: FormSelectOptionsList<string, AdsLiveSearchOptionExtras>
}
